import { Card, StatusBadge, TrustScoreBadge, Button } from "@/components/shared";
import { VerificationBadge } from "@/components/shared/VerificationBadge";
import { TrustScoreDisplay } from "@/components/shared/TrustScoreDisplay";
import type { PPRAStatus, AgentBadge } from "@/types";
import { Award, MapPin, Zap, Star, ClipboardCheck, CheckCircle2 } from "lucide-react";

const badgeDisplay: Record<AgentBadge, { label: string; icon: React.ComponentType<{ className?: string }>; className: string }> = {
  ppra_verified: { label: "PPRA Verified", icon: Award, className: "text-green-400 bg-green-500/10 border-green-500/20" },
  trusted_pro: { label: "Trusted Pro", icon: Star, className: "text-purple-400 bg-purple-500/10 border-purple-500/20" },
  local_specialist: { label: "Local Specialist", icon: MapPin, className: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  fast_responder: { label: "Fast Responder", icon: Zap, className: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20" },
  top_rated: { label: "Top Rated", icon: Star, className: "text-gold bg-gold/10 border-gold/20" },
};

interface AgentInfo {
  id: string;
  display_name: string;
  agency_name: string;
  ppra_status: string;
  years_experience: number | null;
  bio: string | null;
  profile_photo_url: string | null;
  primary_region: string;
  trust_score: number;
  avg_response_time_mins: number | null;
  total_responses: number;
  total_unlocks: number;
  badges?: AgentBadge[];
}

interface ResponseData {
  id: string;
  request_id: string;
  agent_id: string;
  valuation_min: number;
  valuation_max: number;
  demand_level: string;
  comparable_sales: Array<{ address: string; price: number; date: string }>;
  strategy_summary: string;
  commission_rate: number | null;
  ranking_score: number;
  created_at: string;
  agent: AgentInfo;
}

interface Props {
  response: ResponseData;
  onSelect?: (responseId: string) => void;
  onDismiss?: (responseId: string) => void;
  onReinvite?: (selectionId: string) => void;
  isSelected?: boolean;
  isInvited?: boolean;
  selectionStatus?: string | null;
  leadStatus?: string | null;
  selectionDisabled?: boolean;
}

export function AgentResponseCard({ response, onSelect, onDismiss, onReinvite, isSelected, isInvited, selectionStatus, leadStatus, selectionDisabled }: Props) {
  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(cents);

  const demandColors: Record<string, string> = {
    low: "bg-yellow-100 text-yellow-800",
    medium: "bg-blue-100 text-blue-800",
    high: "bg-green-100 text-green-800",
  };

  return (
    <Card className={`transition-all ${isSelected ? "selected-card-border" : ""}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {response.agent.profile_photo_url ? (
            <img
              src={response.agent.profile_photo_url}
              alt={response.agent.display_name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold text-lg">
              {response.agent.display_name.charAt(0)}
            </div>
          )}
          <div>
            <h3 className="font-semibold text-white">{response.agent.display_name}</h3>
            <p className="text-sm text-white/50">{response.agent.agency_name}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <VerificationBadge status={response.agent.ppra_status as PPRAStatus} showDisclaimer />
          <TrustScoreDisplay score={response.agent.trust_score} size="sm" />
        </div>
      </div>

      {/* Valuation Range */}
      <div className="mb-4">
        <p className="text-sm text-white/50 mb-1">Estimated Value Range</p>
        <p className="text-xl font-bold text-white">
          {formatCurrency(response.valuation_min)} – {formatCurrency(response.valuation_max)}
        </p>
      </div>

      {/* Demand + Commission */}
      <div className="flex items-center gap-3 mb-4">
        <span className={`px-2 py-1 rounded text-xs font-medium ${demandColors[response.demand_level] || ""}`}>
          {response.demand_level.charAt(0).toUpperCase() + response.demand_level.slice(1)} demand
        </span>
        {response.commission_rate && (
          <span className="text-sm text-white/60">
            {response.commission_rate}% commission
          </span>
        )}
      </div>

      {/* Strategy Summary */}
      <div className="mb-4">
        <p className="text-sm text-white/50 mb-1">Strategy</p>
        <p className="text-sm text-white/70">{response.strategy_summary}</p>
      </div>

      {/* Comparable Sales */}
      {response.comparable_sales.length > 0 && (
        <div className="mb-4">
          <p className="text-sm text-white/50 mb-2">Recent Comparable Sales</p>
          <div className="space-y-1">
            {response.comparable_sales.map((sale, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-white/60 truncate max-w-[60%]">{sale.address}</span>
                <span className="text-white font-medium">{formatCurrency(sale.price)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent Stats */}
      <div className="flex gap-4 text-xs text-white/50 border-t pt-3 mb-3">
        <span>{response.agent.years_experience || 0} yrs experience</span>
        <span>{response.agent.total_responses} responses</span>
        {response.agent.avg_response_time_mins && (
          <span>~{Math.round(response.agent.avg_response_time_mins)}min response</span>
        )}
      </div>

      {/* Badges */}
      {response.agent.badges && response.agent.badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {response.agent.badges.filter(b => b !== "ppra_verified").map((badge) => {
            const config = badgeDisplay[badge];
            const Icon = config.icon;
            return (
              <span key={badge} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${config.className}`}>
                <Icon className="h-3 w-3" />
                {config.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Select Button */}
      {isInvited ? (
        <>
          {leadStatus === "contact_shared" ? (
            <div className="w-full text-center py-2 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <span className="text-blue-400 font-bold text-sm flex items-center justify-center gap-1.5"><ClipboardCheck className="h-4 w-4" /> Contact Shared</span>
            </div>
          ) : leadStatus === "completed" ? (
            <div className="w-full text-center py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <span className="text-emerald-400 font-bold text-sm">✅ Completed</span>
            </div>
          ) : selectionStatus === "declined" ? (
            <>
              <div className="w-full text-center py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                <span className="text-red-400 font-bold text-sm">✗ Declined</span>
              </div>
              {onReinvite && (
                <button
                  onClick={() => onReinvite(response.id)}
                  className="w-full mt-2 text-xs text-mint hover:text-mint/80 transition-colors font-medium"
                >
                  Re-invite Agent
                </button>
              )}
            </>
          ) : selectionStatus === "expired" ? (
            <>
              <div className="w-full text-center py-2 rounded-lg bg-white/5 border border-white/10">
                <span className="text-white/40 font-bold text-sm">⏱ Expired</span>
              </div>
              {onReinvite && (
                <button
                  onClick={() => onReinvite(response.id)}
                  className="w-full mt-2 text-xs text-mint hover:text-mint/80 transition-colors font-medium"
                >
                  Re-invite Agent
                </button>
              )}
            </>
          ) : selectionStatus === "accepted" ? (
            <div className="w-full text-center py-2 rounded-lg bg-green-500/10 border border-green-500/30">
              <span className="text-green-400 font-bold text-sm">● Engaging</span>
            </div>
          ) : (
            <div className="w-full text-center py-2 rounded-lg bg-mint/10 border border-mint/30">
              <span className="shimmer-text font-bold text-sm">✓ Invited</span>
            </div>
          )}
          {onDismiss && leadStatus !== "completed" && leadStatus !== "contact_shared" && selectionStatus !== "declined" && selectionStatus !== "expired" && (
            <button
              onClick={() => onDismiss(response.id)}
              className="w-full mt-2 text-xs text-red-400/50 hover:text-red-400 transition-colors"
            >
              Dismiss Agent
            </button>
          )}
        </>
      ) : onSelect && (
        <Button
          onClick={() => onSelect(response.id)}
          disabled={selectionDisabled && !isSelected}
          variant={isSelected ? "secondary" : "primary"}
          className="w-full"
        >
          {isSelected ? <span className="shimmer-text font-bold">✓ Selected</span> : "Select This Agent"}
        </Button>
      )}
    </Card>
  );
}
